/**
 * CRMRichiesteUnifiedPage - Admin "Richieste Unificate" (v1.73.0)
 *
 * Consuma /api/admin/richieste (aggregation professionale cross-ramo
 *   con masking PII, RBAC, bulk-actions, audit log, assignment).
 *
 * 8 filtri professionali:
 *   Search (q) · Sort (sort_by/sort_dir) · In Convenzione · Tipo prodotto (ramo)
 *   · Status · Date range · Partner · Assigned To
 *
 * URL persistente (query-string sync).
 */
import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Search, Filter, Users, Calendar, Handshake, ArrowUpDown,
  ChevronLeft, ChevronRight, RotateCcw, X, Loader2,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

// ─────────────────────────────────────────────────────────────
// Palette badge — ramo & status
// ─────────────────────────────────────────────────────────────
const RAMO_STYLES = {
  "Commercio":         "bg-blue-100 text-blue-900 ring-blue-200",
  "D&O":               "bg-purple-100 text-purple-900 ring-purple-200",
  "Cyber":             "bg-emerald-100 text-emerald-900 ring-emerald-200",
  "Fine Arts":         "bg-amber-100 text-amber-900 ring-amber-200",
  "RCA":               "bg-slate-100 text-slate-900 ring-slate-200",
  "Salute":            "bg-rose-100 text-rose-900 ring-rose-200",
  "Salute (RiS)":      "bg-pink-100 text-pink-900 ring-pink-200",
  "Fondi Pensione":    "bg-indigo-100 text-indigo-900 ring-indigo-200",
  "RC Professionale":  "bg-sky-100 text-sky-900 ring-sky-200",
  "Casa":              "bg-teal-100 text-teal-900 ring-teal-200",
  "Infortuni":         "bg-orange-100 text-orange-900 ring-orange-200",
  "Vita":              "bg-violet-100 text-violet-900 ring-violet-200",
  "Viaggi":            "bg-cyan-100 text-cyan-900 ring-cyan-200",
};

const STATUS_STYLES = {
  nuovo:            "bg-blue-100 text-blue-800 ring-blue-200",
  in_lavorazione:   "bg-orange-100 text-orange-800 ring-orange-200",
  preventivato:     "bg-purple-100 text-purple-800 ring-purple-200",
  chiuso_positivo:  "bg-emerald-100 text-emerald-800 ring-emerald-200",
  chiuso_negativo:  "bg-zinc-100 text-zinc-700 ring-zinc-200",
  archiviato:       "bg-zinc-200 text-zinc-600 ring-zinc-300",
};

const STATUS_LABELS = {
  nuovo:           "Nuovo",
  in_lavorazione:  "In lavorazione",
  preventivato:    "Preventivato",
  chiuso_positivo: "Chiuso vinto",
  chiuso_negativo: "Chiuso perso",
  archiviato:      "Archiviato",
};

const RAMI = [
  "Commercio", "D&O", "Cyber", "Fine Arts", "RCA",
  "Salute", "Salute (RiS)", "Fondi Pensione", "RC Professionale",
  "Casa", "Infortuni", "Vita", "Viaggi",
];

const SORT_OPTS = [
  { value: "created_at:desc", label: "Data (più recenti)" },
  { value: "created_at:asc",  label: "Data (più vecchie)" },
  { value: "ramo:asc",        label: "Ramo A→Z" },
  { value: "status:asc",      label: "Status A→Z" },
  { value: "nome:asc",        label: "Cliente A→Z" },
];

// ─────────────────────────────────────────────────────────────
// Query-string helpers
// ─────────────────────────────────────────────────────────────
function qsToState(search) {
  const p = new URLSearchParams(search);
  return {
    q:              p.get("q") || "",
    ramo:           p.get("ramo") || "",
    status:         p.get("status") || "",
    partner:        p.get("partner") || "",
    assigned_to:    p.get("assigned_to") || "",
    date_from:      p.get("date_from") || "",
    date_to:        p.get("date_to") || "",
    in_convenzione: p.get("in_convenzione") || "",       // "" | "true" | "false"
    sort:           p.get("sort") || "created_at:desc",
    page:           parseInt(p.get("page") || "1", 10),
  };
}

function stateToQs(s) {
  const p = new URLSearchParams();
  if (s.q)              p.set("q", s.q);
  if (s.ramo)           p.set("ramo", s.ramo);
  if (s.status)         p.set("status", s.status);
  if (s.partner)        p.set("partner", s.partner);
  if (s.assigned_to)    p.set("assigned_to", s.assigned_to);
  if (s.date_from)      p.set("date_from", s.date_from);
  if (s.date_to)        p.set("date_to", s.date_to);
  if (s.in_convenzione) p.set("in_convenzione", s.in_convenzione);
  if (s.sort && s.sort !== "created_at:desc") p.set("sort", s.sort);
  if (s.page && s.page > 1) p.set("page", String(s.page));
  return p.toString();
}

// ─────────────────────────────────────────────────────────────
// Componente principale
// ─────────────────────────────────────────────────────────────
export default function CRMRichiesteUnifiedPage() {
  const navigate = useNavigate();
  const location = useLocation();

  const initial = useMemo(() => qsToState(location.search), []); // eslint-disable-line
  const [filters, setFilters] = useState(initial);
  const [pageSize] = useState(25);
  const [data, setData] = useState({ total: 0, items: [], unified_statuses: [] });
  const [loading, setLoading] = useState(false);
  const [dipendenti, setDipendenti] = useState([]);

  // ─── URL sync ─────────────────────────────────────────────
  useEffect(() => {
    const qs = stateToQs(filters);
    navigate({ pathname: location.pathname, search: qs ? `?${qs}` : "" },
             { replace: true });
  }, [filters]); // eslint-disable-line

  // ─── Load dipendenti (per dropdown assigned_to) ───────────
  useEffect(() => {
    axios.get(`${API}/api/admin/team/dipendenti-attivi`, { withCredentials: true })
      .then(r => setDipendenti(r.data?.users || []))
      .catch(() => setDipendenti([]));
  }, []);

  // ─── Load richieste ───────────────────────────────────────
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [sortBy, sortDir] = (filters.sort || "created_at:desc").split(":");
      const params = {
        page: filters.page || 1,
        page_size: pageSize,
        sort_by: sortBy,
        sort_dir: sortDir,
      };
      if (filters.q)           params.q = filters.q;
      if (filters.ramo)        params.ramo = filters.ramo;
      if (filters.status)      params.status = filters.status;
      if (filters.partner)     params.partner = filters.partner;
      if (filters.assigned_to) params.assigned_to = filters.assigned_to;
      if (filters.date_from)   params.date_from = filters.date_from;
      if (filters.date_to)     params.date_to = filters.date_to;
      if (filters.in_convenzione === "true")  params.in_convenzione = true;
      if (filters.in_convenzione === "false") params.in_convenzione = false;

      const r = await axios.get(`${API}/api/admin/richieste`,
                                 { params, withCredentials: true });
      setData(r.data);
    } catch (e) {
      toast.error("Errore caricamento richieste: " +
                     (e.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  }, [filters, pageSize]);

  useEffect(() => { load(); }, [load]);

  const setF = (patch) => setFilters(f => ({ ...f, ...patch, page: 1 }));
  const resetFilters = () => setFilters({
    q: "", ramo: "", status: "", partner: "", assigned_to: "",
    date_from: "", date_to: "", in_convenzione: "",
    sort: "created_at:desc", page: 1,
  });

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil((data.total || 0) / pageSize)),
    [data.total, pageSize],
  );

  const activeFilterCount = useMemo(() => {
    let n = 0;
    if (filters.q) n++;
    if (filters.ramo) n++;
    if (filters.status) n++;
    if (filters.partner) n++;
    if (filters.assigned_to) n++;
    if (filters.date_from || filters.date_to) n++;
    if (filters.in_convenzione) n++;
    return n;
  }, [filters]);

  return (
    <div className="space-y-5" data-testid="richieste-unified-page">
      {/* ═══════════════════ Header ═══════════════════ */}
      <header className="flex items-baseline justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-emerald-900 tracking-tight">
            Richieste Unificate
          </h1>
          <p className="text-sm text-zinc-600 mt-0.5">
            Vista aggregata cross-ramo di tutte le richieste (Commercio, D&amp;O,
            Cyber, Fine Arts, RCA, Sanitarie, Convenzioni, Fondi Pensione, ecc.).
          </p>
        </div>
        <div className="text-sm text-zinc-500" data-testid="richieste-total-count">
          Totale filtrato:{" "}
          <span className="font-semibold text-emerald-900">{data.total || 0}</span>
        </div>
      </header>

      {/* ═══════════════════ Barra filtri ═══════════════════ */}
      <div className="bg-white rounded-xl ring-1 ring-zinc-200 p-4 space-y-3"
           data-testid="richieste-filters-panel">
        {/* Riga 1: Search + Sort + Reset */}
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[240px]">
            <label className="text-xs text-zinc-600 flex items-center gap-1.5">
              <Search className="w-3.5 h-3.5" /> Ricerca
            </label>
            <input
              type="text"
              value={filters.q}
              onChange={(e) => setFilters(f => ({ ...f, q: e.target.value }))}
              onKeyDown={(e) => e.key === "Enter" && setF({ q: filters.q })}
              placeholder="Nome, email, CF, telefono, targa, numero pratica…"
              className="mt-1 w-full px-3 py-2 border border-zinc-200 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
              data-testid="filter-q"
            />
          </div>
          <div>
            <label className="text-xs text-zinc-600 flex items-center gap-1.5">
              <ArrowUpDown className="w-3.5 h-3.5" /> Ordinamento
            </label>
            <select
              value={filters.sort}
              onChange={(e) => setF({ sort: e.target.value })}
              className="mt-1 px-3 py-2 border border-zinc-200 rounded-lg text-sm bg-white"
              data-testid="filter-sort"
            >
              {SORT_OPTS.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
          <button
            onClick={resetFilters}
            disabled={activeFilterCount === 0}
            className="px-3 py-2 text-sm rounded-lg ring-1 ring-zinc-200 bg-white hover:bg-zinc-50 disabled:opacity-40 flex items-center gap-1.5"
            data-testid="filter-reset"
          >
            <RotateCcw className="w-3.5 h-3.5" />
            Reset {activeFilterCount > 0 && `(${activeFilterCount})`}
          </button>
        </div>

        {/* Riga 2: Convenzione toggle + Ramo + Status + Partner + Assigned + Date */}
        <div className="flex flex-wrap items-end gap-3">
          {/* In Convenzione */}
          <div>
            <label className="text-xs text-zinc-600 flex items-center gap-1.5">
              <Handshake className="w-3.5 h-3.5" /> Canale
            </label>
            <div className="mt-1 inline-flex rounded-lg ring-1 ring-zinc-200 overflow-hidden bg-white"
                 data-testid="filter-in-convenzione-group">
              {[
                { v: "",      label: "Tutti" },
                { v: "true",  label: "In Convenzione" },
                { v: "false", label: "Diretto" },
              ].map(opt => (
                <button
                  key={opt.v || "all"}
                  onClick={() => setF({ in_convenzione: opt.v })}
                  className={`px-3 py-2 text-xs font-medium transition-colors ${
                    filters.in_convenzione === opt.v
                      ? "bg-amber-500 text-white"
                      : "text-zinc-600 hover:bg-zinc-50"
                  }`}
                  data-testid={`filter-in-convenzione-${opt.v || "all"}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
          {/* Ramo */}
          <div>
            <label className="text-xs text-zinc-600 flex items-center gap-1.5">
              <Filter className="w-3.5 h-3.5" /> Tipo prodotto
            </label>
            <select
              value={filters.ramo}
              onChange={(e) => setF({ ramo: e.target.value })}
              className="mt-1 px-3 py-2 border border-zinc-200 rounded-lg text-sm bg-white min-w-[160px]"
              data-testid="filter-ramo"
            >
              <option value="">Tutti i rami</option>
              {RAMI.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          {/* Status */}
          <div>
            <label className="text-xs text-zinc-600">Status</label>
            <select
              value={filters.status}
              onChange={(e) => setF({ status: e.target.value })}
              className="mt-1 px-3 py-2 border border-zinc-200 rounded-lg text-sm bg-white"
              data-testid="filter-status"
            >
              <option value="">Tutti</option>
              {Object.entries(STATUS_LABELS).map(([v, l]) => (
                <option key={v} value={v}>{l}</option>
              ))}
            </select>
          </div>
          {/* Partner */}
          <div>
            <label className="text-xs text-zinc-600">Partner</label>
            <input
              type="text"
              value={filters.partner}
              onChange={(e) => setFilters(f => ({ ...f, partner: e.target.value }))}
              onKeyDown={(e) => e.key === "Enter" && setF({ partner: filters.partner })}
              placeholder="stellantis, leonardo…"
              className="mt-1 px-3 py-2 border border-zinc-200 rounded-lg text-sm w-[160px]"
              data-testid="filter-partner"
            />
          </div>
          {/* Assigned To */}
          <div>
            <label className="text-xs text-zinc-600 flex items-center gap-1.5">
              <Users className="w-3.5 h-3.5" /> Assegnato a
            </label>
            <select
              value={filters.assigned_to}
              onChange={(e) => setF({ assigned_to: e.target.value })}
              className="mt-1 px-3 py-2 border border-zinc-200 rounded-lg text-sm bg-white min-w-[160px]"
              data-testid="filter-assigned-to"
            >
              <option value="">Chiunque</option>
              <option value="me">A me</option>
              <option value="unassigned">Non assegnate</option>
              {dipendenti.map(u => (
                <option key={u.user_id} value={u.user_id}>
                  {u.name || u.email}
                </option>
              ))}
            </select>
          </div>
          {/* Date range */}
          <div>
            <label className="text-xs text-zinc-600 flex items-center gap-1.5">
              <Calendar className="w-3.5 h-3.5" /> Dal
            </label>
            <input
              type="date"
              value={filters.date_from}
              onChange={(e) => setF({ date_from: e.target.value })}
              className="mt-1 px-3 py-2 border border-zinc-200 rounded-lg text-sm"
              data-testid="filter-date-from"
            />
          </div>
          <div>
            <label className="text-xs text-zinc-600">Al</label>
            <input
              type="date"
              value={filters.date_to}
              onChange={(e) => setF({ date_to: e.target.value })}
              className="mt-1 px-3 py-2 border border-zinc-200 rounded-lg text-sm"
              data-testid="filter-date-to"
            />
          </div>
        </div>
      </div>

      {/* ═══════════════════ Tabella ═══════════════════ */}
      <div className="bg-white rounded-xl ring-1 ring-zinc-200 overflow-hidden"
           data-testid="richieste-table-wrap">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-zinc-600 text-xs uppercase tracking-wide">
              <tr>
                <th className="text-left px-4 py-3">Data</th>
                <th className="text-left px-4 py-3">Ramo</th>
                <th className="text-left px-4 py-3">Cliente</th>
                <th className="text-left px-4 py-3">Contatti</th>
                <th className="text-left px-4 py-3">Partner</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-left px-4 py-3">Assegnato</th>
                <th className="text-left px-4 py-3">Pratica</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={8} className="text-center py-12 text-zinc-400">
                    <Loader2 className="w-5 h-5 mx-auto animate-spin" />
                  </td>
                </tr>
              )}
              {!loading && (data.items?.length || 0) === 0 && (
                <tr>
                  <td colSpan={8} className="text-center py-12 text-zinc-400"
                      data-testid="richieste-empty-state">
                    Nessuna richiesta trovata con i filtri attuali.
                  </td>
                </tr>
              )}
              {!loading && (data.items || []).map((r) => (
                <tr
                  key={`${r.module}-${r.id}`}
                  className="border-t border-zinc-100 hover:bg-emerald-50/40 transition"
                  data-testid={`richiesta-row-${r.id}`}
                >
                  <td className="px-4 py-3 text-zinc-600 whitespace-nowrap">
                    {r.created_at
                      ? new Date(r.created_at).toLocaleDateString("it-IT",
                          { day: "2-digit", month: "2-digit", year: "2-digit" })
                      : "—"}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-col gap-1">
                      <span className={`px-2 py-0.5 text-xs rounded-md ring-1 inline-block w-fit ${
                        RAMO_STYLES[r.ramo] || "bg-zinc-100 text-zinc-800 ring-zinc-200"
                      }`}>
                        {r.ramo}
                      </span>
                      {r.in_convenzione && (
                        <span className="px-2 py-0.5 text-[10px] rounded-md ring-1 bg-yellow-100 text-yellow-900 ring-yellow-200 inline-block w-fit font-medium"
                              data-testid={`badge-convenzione-${r.id}`}>
                          IN CONVENZIONE
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 font-medium text-zinc-900">
                    {[r.nome, r.cognome].filter(Boolean).join(" ") || "—"}
                    {r.cf_masked && (
                      <div className="text-[11px] text-zinc-500 font-mono">
                        {r.cf_masked}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-zinc-700 text-xs">
                    {r.email_masked && <div>{r.email_masked}</div>}
                    {r.tel_masked && <div className="text-zinc-500">{r.tel_masked}</div>}
                    {!r.email_masked && !r.tel_masked && "—"}
                  </td>
                  <td className="px-4 py-3 text-xs text-zinc-600">
                    {r.partner || <span className="text-zinc-400">—</span>}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 text-xs rounded-md ring-1 ${
                      STATUS_STYLES[r.status_unified] || "bg-zinc-100 text-zinc-700 ring-zinc-200"
                    }`}>
                      {STATUS_LABELS[r.status_unified] || r.status_unified}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-zinc-600">
                    {r.assigned_to
                      ? (dipendenti.find(u => u.user_id === r.assigned_to)?.name
                         || r.assigned_to)
                      : <span className="text-zinc-400">—</span>}
                  </td>
                  <td className="px-4 py-3 text-xs font-mono text-zinc-500">
                    {r.numero_pratica || (r.id || "").slice(0, 8)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ═══════════════════ Paginazione ═══════════════════ */}
      <div className="flex items-center justify-between text-sm text-zinc-600">
        <div>
          Pagina <strong>{filters.page}</strong> di <strong>{totalPages}</strong>{" "}
          · {data.total || 0} richieste totali
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setFilters(f => ({ ...f, page: Math.max(1, f.page - 1) }))}
            disabled={filters.page <= 1}
            className="px-3 py-1.5 ring-1 ring-zinc-200 rounded-lg bg-white hover:bg-zinc-50 disabled:opacity-40 flex items-center gap-1"
            data-testid="page-prev"
          >
            <ChevronLeft className="w-4 h-4" /> Indietro
          </button>
          <button
            onClick={() => setFilters(f => ({ ...f, page: Math.min(totalPages, f.page + 1) }))}
            disabled={filters.page >= totalPages}
            className="px-3 py-1.5 ring-1 ring-zinc-200 rounded-lg bg-white hover:bg-zinc-50 disabled:opacity-40 flex items-center gap-1"
            data-testid="page-next"
          >
            Avanti <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
